"use client";

import Loading from "components/Loading";
import { useUser } from "hooks/useUser";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import {
  consumeIntentionalLogout,
  getAuthenticatedHomePath,
  getLoginPathWithRedirect,
  getSafePostLoginPath,
  getStoredAuthUser,
} from "utils/authRoutes";

export default function ProtectedGuard({
  children,
}: {
  children: React.ReactNode;
}) {
  const { verifyUser, authUser } = useUser();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const publicRoutes = ["/login", "/forget-password", "/reset-password"];
      const isPublicRoute =
        pathname === "/" ||
        publicRoutes.some((route) => pathname?.startsWith(route));
      const currentAuthUser = authUser ?? getStoredAuthUser();
      const search = searchParams.toString();
      const currentPath = search ? `${pathname}?${search}` : pathname;

      const redirect = verifyUser();
      if (redirect === "login") {
        if (isPublicRoute && pathname !== "/") {
          setAuthorized(true);
        } else {
          setAuthorized(false);
          if (consumeIntentionalLogout()) {
            router.replace("/login");
          } else {
            router.replace(getLoginPathWithRedirect(currentPath));
          }
        }
        return;
      }

      if (pathname === "/login") {
        router.replace(
          getSafePostLoginPath(
            searchParams.get("redirect"),
            currentAuthUser,
          ),
        );
      } else if (pathname === "/") {
        router.replace(getAuthenticatedHomePath(currentAuthUser));
      } else if (pathname === "/password-expired") {
        setAuthorized(true);
      } else {
        if (pathname === "/home" && currentAuthUser?.roles !== "super_admin") {
          router.replace(getAuthenticatedHomePath(currentAuthUser));
        } else {
          setAuthorized(true);
        }
      }
    }
  }, [verifyUser, router, pathname, searchParams, authUser]);

  if (!authorized) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  return children;
}
